/**
 * The site's horizontal rhythm, as class strings.
 *
 * Every full-width band used to invent its own container and padding —
 * `px-6 md:px-10` here, `px-5 lg:px-8` there, three different max widths — so
 * headings on adjacent sections never lined up. Chrome (header, footer) reads
 * from here; page sections adopt it as each one is reworked.
 */

/**
 * Gutter, matched across breakpoints. Mobile is 20px: enough to clear a thumb
 * resting on the edge of a 375px screen without eating the content.
 *
 * It used to open up to 40px at `lg`. That was sized when this gutter framed
 * text bands and nothing else — the bento and the header sat outside it on
 * their own 12/16px frame, so 40 never had to agree with anything. Now that
 * every band including the bento is inside {@link CONTAINER}, 40px is the wrong
 * frame for a grid of image tiles: it reads as a page inset around the cards
 * rather than as the page's edge. 24 from `sm` up holds the edge for prose and
 * still lets the tiles run close to it.
 */
export const GUTTER = 'px-5 sm:px-6'

/**
 * The app's ONE content column. Header, bento, every section, the footer.
 *
 * There were two systems before this, and they disagreed by a growing amount:
 * text bands sat in `max-w-[90rem]` with a 40px gutter, while the header and
 * the home bento were deliberately pulled OUT of the container onto a 12/16px
 * full-bleed frame so the bar would read as the bento's top row. At 1440 that
 * put two different left edges 24px apart on the same page. At 1920 the bento
 * ran 16 → 1904 and every other band 240 → 1680: a 224px step, which is what
 * it looks like when a page has no layout.
 *
 * `110rem` (1760px), raised from 90rem, because unifying the two had to happen
 * at the WIDER of them — the bento is the page's first act and capping it at
 * the old text measure would have shrunk the thing everything else was being
 * asked to line up with. Below 1760 nothing caps at all and the page is simply
 * fluid inside the gutter; above it, everything centres together.
 *
 * A page that needs a reading measure narrower than this asks for one INSIDE
 * it — see {@link PANEL_COLUMN}. The container is the frame, not the measure.
 */
export const CONTAINER = `mx-auto w-full max-w-[110rem] ${GUTTER}`

/**
 * The account area's reading measure — forms, statements and conversations.
 *
 * Deliberately far narrower than {@link CONTAINER}: a text input the width of a
 * 1440px screen is a target nobody aims at, and a label/value line that wide
 * puts its two halves in different postcodes.
 *
 * It is the EXCEPTION, not the panel's default. A list, a grid or a calendar
 * takes the container's measure — the same one the panel's own navigation runs
 * in — because two different edges on one page is what it looks like when a
 * page has no layout, and that is exactly what the panel looked like: a nav bar
 * spanning 1760px above content stopped at 896px, with the difference growing
 * as the screen got wider.
 *
 * A screen asks for this measure by dropping a `layout.tsx` into its segment
 * that re-exports `PanelReadingColumn`. Never by wrapping its own body: Next
 * renders `loading.tsx` inside the layout, so the layout is the only place that
 * makes a skeleton and its page agree on width by construction.
 */
export const PANEL_COLUMN = 'max-w-4xl'

/**
 * The panel's shell: navigation column, then content.
 *
 * A GRID inside {@link CONTAINER}, not a fixed rail beside it. The footer is a
 * sibling of `<main>`, so a full-height fixed sidebar would have nothing to
 * end against and would run straight over it; and the header, the footer and
 * the panel have to share one left edge — a sidebar with its own measure puts
 * two different edges on the same page.
 *
 * Below `lg` there is no grid at all: the navigation goes back to being the
 * horizontal strip it has always been on a phone, where a 15rem column would
 * leave no room for the work.
 */
export const PANEL_SHELL_GRID =
  'lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-8 xl:grid-cols-[17rem_minmax(0,1fr)] xl:gap-10'

/**
 * The first band on a page that is not the home page.
 *
 * The header is fixed, so a page's own content has to start below it. Every
 * service page hardcoded `pt-32 sm:pt-36` for that clearance — 128px on a
 * phone, against a header that measures 72px including its offset. The
 * difference was not breathing room, it was a gap nobody chose.
 *
 * The BOTTOM half had the same problem from the other end, and it was the
 * larger one. `SiteFooter` carries its own `mt-24`, so a page closing on
 * `pb-24` stacked 96px on 96px: every service page ended with 192px of nothing
 * between its last line and the footer's rule. Neither value was wrong on its
 * own, which is why it survived — the footer owns the separation from whatever
 * precedes it, so the page only has to make sure its content does not sit ON
 * that rule. 24/40px does that, and the pair now reads as ~136px total.
 */
export const PAGE_TOP = 'pt-28 pb-6 sm:pt-32 sm:pb-10'

/**
 * {@link PAGE_TOP} for a page that has to fit its first screen.
 *
 * Same job — clear the fixed header, stay off the footer's rule — but sized in
 * `svh` rather than in one fixed step, because on an offer page this clearance
 * is competing with the thing the reader came for. 128px of air above a
 * headline is right on a 1440-tall monitor and is a tenth of a laptop's screen
 * spent on nothing.
 *
 * The floor is 80px. The fixed bar runs 16 → 66px down the window, so that is
 * the page's own rule sitting 14px under it — close, and deliberately so: on a
 * screen short enough to reach this floor, every one of those pixels is one the
 * ladder does not have.
 */
export const SCREEN_TOP = 'pt-[clamp(5rem,10svh,8rem)] pb-[clamp(0.75rem,3svh,2.5rem)]'

/**
 * The block that OWNS the first screen, inside a {@link SCREEN_TOP} container.
 *
 * `min-h-[100svh]` on the container itself does nothing useful, and that is
 * worth writing down: the container holds the WHOLE page, so it is already
 * taller than a screen and a `flex-1` child has no free space to grow into.
 * What has to be a screen tall is the first band — the head and the thing it
 * introduces — with everything below it left outside.
 *
 * Hence the subtraction: a screen, less the clearance over the head and the
 * air under it, both of which the container is already spending. The two
 * `clamp()`s are {@link SCREEN_TOP}'s, and they have to stay in step with it —
 * which is why they are written here rather than guessed at a call site.
 *
 * A child of this block with `flex-1` takes whatever the head leaves, so the
 * next section starts below the fold instead of peeking over it. Owner's call
 * on `/pl/studio`: *„te panele dłuższe mogą być, żeby nie było widać tego «po
 * nagraniu»"*.
 */
export const SCREEN_FILL =
  'flex min-h-[calc(100svh_-_clamp(5rem,10svh,8rem)_-_clamp(0.75rem,3svh,2.5rem))] flex-col'

/**
 * {@link SCREEN_FILL} where the screen is a CEILING as well as a floor.
 *
 * `min-h` alone lets a block grow past the window, which is right for a page
 * that READS down a column: the content that does not fit pushes the page
 * taller and the reader scrolls to it. It is wrong for a page laid out ACROSS
 * columns — `/pl/fotograf-operator` — where one long column would drag the other two
 * off the bottom of the screen with it, and the whole point of the layout is
 * that all three are in sight at once.
 *
 * So from `lg` the height is definite, and a column whose content genuinely
 * exceeds it scrolls INSIDE ITSELF. That needs the definite height: an
 * `overflow-y-auto` child of an auto-height parent never scrolls, it just makes
 * its parent taller. Nothing is clipped and nothing is hidden — the overflow
 * moves from the page to the one panel that owns it.
 *
 * `xl`, not `lg`, and that is a measurement rather than a preference: at
 * 1024px the three columns came out 278 / 361 / 292 wide, and at 278 the
 * specialist card's name wrapped into its own price while the calendar
 * overflowed its panel by 114px. 1280 is the first width where three columns
 * are three usable ones. Below it this is exactly {@link SCREEN_FILL} — the
 * columns stack, the page scrolls, and a screen-tall cap would be a cage.
 */
export const SCREEN_LOCK = `${SCREEN_FILL} xl:h-[calc(100svh_-_clamp(5rem,10svh,8rem)_-_clamp(0.75rem,3svh,2.5rem))]`

/**
 * The contact stepper's in-page anchor.
 *
 * Here rather than in either file that uses it: the stepper is rendered by a
 * `'use client'` page and the link to it lives in a server component, so
 * exporting the constant from either one would drag that file's half of the
 * boundary into the other's graph for the sake of a string. `scroll-mt` on
 * the stepper itself keeps the landing clear of the fixed header.
 */
export const BOOKING_ANCHOR = 'umow-spotkanie'
